import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Fade,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QuizIcon from '@mui/icons-material/Quiz';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCourses } from '../store/thunks/coursesThunks';
import { setSelectedCategory } from '@e-learning/shared/src/store/slices/coursesSlice';
import QuizCard from '../components/quizzes/QuizCard';
import { fetchQuestionsByCourseId } from '../services/firestore/questionService';

const QuizListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { courses, loading, error, selectedCategory } = useAppSelector((state) => state.courses);
  const [searchQuery, setSearchQuery] = useState('');
  const [coursesWithQuestions, setCoursesWithQuestions] = useState<
    Array<{ course: any; questionCount: number }>
  >([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  useEffect(() => {
    dispatch(fetchCourses());
  }, [dispatch]);

  useEffect(() => {
    const loadQuestionCounts = async () => {
      setLoadingQuestions(true);
      const coursesWithCounts = await Promise.all(
        courses.map(async (course) => {
          try {
            const questions = await fetchQuestionsByCourseId(course.id);
            return {
              course,
              questionCount: questions.length,
            };
          } catch (error) {
            return {
              course,
              questionCount: 0,
            };
          }
        })
      );
      // Only show courses that have questions
      setCoursesWithQuestions(coursesWithCounts.filter((item) => item.questionCount > 0));
      setLoadingQuestions(false);
    };

    if (courses.length > 0) {
      loadQuestionCounts();
    } else {
      setLoadingQuestions(false);
    }
  }, [courses]);

  // Extract unique categories from courses
  const categories = Array.from(new Set(courses.map((course) => course.category))).filter(Boolean);

  // Filter courses based on category and search
  const filteredCourses = coursesWithQuestions.filter(({ course }) => {
    const matchesCategory = !selectedCategory || course.category === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header Section */}
      <Fade in timeout={600}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
              mb: 3,
            }}
          >
            <QuizIcon sx={{ fontSize: 60, color: 'primary.main' }} />
          </Box>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Available Quizzes
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            Test your knowledge and track your progress with our comprehensive course quizzes
          </Typography>
        </Box>
      </Fade>

      {/* Search and Filter Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            placeholder="Search quizzes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                background: 'rgba(30, 41, 59, 0.5)',
                '&:hover': {
                  background: 'rgba(30, 41, 59, 0.7)',
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Chip
              label="All"
              onClick={() => dispatch(setSelectedCategory(null))}
              color={!selectedCategory ? 'primary' : 'default'}
              sx={{
                cursor: 'pointer',
                ...(!selectedCategory && {
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                }),
              }}
            />
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => dispatch(setSelectedCategory(category))}
                color={selectedCategory === category ? 'primary' : 'default'}
                sx={{
                  cursor: 'pointer',
                  ...(selectedCategory === category && {
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.3)',
                  }),
                }}
              />
            ))}
          </Box>
        </Box>
      </Fade>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {(loading || loadingQuestions) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Quiz Cards Grid */}
      {!loading && !loadingQuestions && filteredCourses.length > 0 && (
        <Grid container spacing={4}>
          {filteredCourses.map(({ course, questionCount }, index) => (
            <Grid item xs={12} sm={6} md={4} key={course.id}>
              <QuizCard course={course} index={index} questionCount={questionCount} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!loading && !loadingQuestions && filteredCourses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
            No quizzes available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {searchQuery || selectedCategory
              ? 'Try adjusting your search or filter criteria'
              : 'Quizzes will appear here once questions are added to courses'}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default QuizListPage;

